import { useRef, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Cloud, FolderOpen, UploadCloud, Lock } from "lucide-react";

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const EXT = [".jpg", ".jpeg", ".png", ".webp"];

function loadScript(src, id, dataset = {}) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.id = id;
    Object.entries(dataset).forEach(([k, v]) => (s.dataset[k] = v));
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Kon script niet laden: ${id}`));
    document.head.appendChild(s);
  });
}

function SourceCard({ icon: Icon, title, desc, onClick, disabled, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="group relative flex flex-1 flex-col items-center gap-2 rounded-2xl border border-re-line bg-white p-6 text-center transition-all hover:border-re-red/40 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
    >
      {badge && (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
          <Lock className="h-2.5 w-2.5" /> {badge}
        </span>
      )}
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-re-red/10 text-re-red">
        <Icon className="h-6 w-6" />
      </span>
      <span className="font-heading text-sm font-semibold text-re-charcoal">{title}</span>
      <span className="text-xs text-re-muted">{desc}</span>
    </button>
  );
}

export function ImportSources({ config, onAddFiles, currentCount, maxPhotos }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const driveConfigured = !!config?.googleDrive?.configured;
  const dropboxConfigured = !!config?.dropbox?.configured;

  const filterAndSend = (items) => {
    // items: [{file, filename, source, type, size}]
    const accepted = [];
    const rejected = [];
    let room = maxPhotos - currentCount;
    items.forEach((it) => {
      if (accepted.length >= room) return rejected.push(`${it.filename}: maximum van ${maxPhotos} foto's bereikt`);
      const type = (it.type || "").toLowerCase();
      const extOk = EXT.some((e) => (it.filename || "").toLowerCase().endsWith(e));
      if (!ACCEPTED.includes(type) && !extOk) return rejected.push(`${it.filename}: type niet ondersteund`);
      if (it.size > 20 * 1024 * 1024) return rejected.push(`${it.filename}: groter dan 20 MB`);
      accepted.push(it);
    });
    onAddFiles(accepted, rejected);
  };

  const handleLocal = (fileList) => {
    const items = Array.from(fileList || []).map((f) => ({ file: f, filename: f.name, source: "computer", type: f.type, size: f.size }));
    filterAndSend(items);
    if (inputRef.current) inputRef.current.value = "";
  };

  // ---- Google Drive (GIS token + Picker) ----
  const openDrive = async () => {
    if (!driveConfigured) return;
    try {
      const { clientId, apiKey, projectNumber } = config.googleDrive;
      await Promise.all([
        loadScript("https://accounts.google.com/gsi/client", "gis"),
        loadScript("https://apis.google.com/js/api.js", "gapi"),
      ]);
      await new Promise((r) => window.gapi.load("picker", r));
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "https://www.googleapis.com/auth/drive.file",
        callback: async (resp) => {
          if (resp.error) return toast.error("Google-toegang geweigerd of geannuleerd.");
          const token = resp.access_token;
          try {
            const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
            view.setMimeTypes(ACCEPTED.join(","));
            const builder = new window.google.picker.PickerBuilder()
              .setDeveloperKey(apiKey)
              .setOAuthToken(token)
              .addView(view)
              .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
              .setCallback(async (data) => {
                if (data.action !== window.google.picker.Action.PICKED) return;
                const docs = data[window.google.picker.Response.DOCUMENTS] || [];
                const items = [];
                for (const doc of docs) {
                  const id = doc[window.google.picker.Document.ID];
                  try {
                    const meta = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?fields=name,mimeType,size`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json());
                    const blob = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?alt=media`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => { if (!r.ok) throw new Error("download"); return r.blob(); });
                    const file = new File([blob], meta.name || "drive-image", { type: meta.mimeType || blob.type });
                    items.push({ file, filename: file.name, source: "drive", type: file.type, size: file.size });
                  } catch {
                    toast.error(`Kon bestand niet importeren uit Google Drive.`);
                  }
                }
                filterAndSend(items);
              })
              .build();
            builder.setVisible(true);
          } finally {
            try { window.google.accounts.oauth2.revoke(token); } catch { /* noop */ }
          }
        },
      });
      if (projectNumber) tokenClient.projectNumber = projectNumber;
      tokenClient.requestAccessToken({ prompt: "" });
    } catch {
      toast.error("Google Drive kon niet worden geopend.");
    }
  };

  // ---- Dropbox Chooser ----
  const openDropbox = async () => {
    if (!dropboxConfigured) return;
    try {
      await loadScript("https://www.dropbox.com/static/api/2/dropins.js", "dropboxjs", { appKey: config.dropbox.appKey });
      if (!window.Dropbox) return toast.error("Dropbox kon niet laden.");
      window.Dropbox.choose({
        linkType: "direct",
        multiselect: true,
        extensions: EXT,
        success: async (files) => {
          const items = [];
          for (const f of files || []) {
            if (!f?.link) continue;
            try {
              const blob = await fetch(f.link).then((r) => { if (!r.ok) throw new Error("dl"); return r.blob(); });
              const file = new File([blob], f.name || "dropbox-image", { type: blob.type || "image/jpeg" });
              items.push({ file, filename: file.name, source: "dropbox", type: file.type, size: file.size || f.bytes });
            } catch {
              toast.error("Kon bestand niet importeren uit Dropbox.");
            }
          }
          filterAndSend(items);
        },
        cancel: () => {},
      });
    } catch {
      toast.error("Dropbox kon niet worden geopend.");
    }
  };

  return (
    <div data-testid="import-sources">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleLocal(e.dataTransfer.files); }}
        className={`rounded-3xl border-2 border-dashed p-5 transition-colors ${dragOver ? "border-re-red bg-re-red/5" : "border-re-line bg-re-soft"}`}
      >
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-re-muted">
          <UploadCloud className="h-4 w-4 text-re-red" /> Sleep foto&apos;s hierheen of kies een bron
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <SourceCard icon={FolderOpen} title="Bestanden kiezen" desc="Vanaf je computer" onClick={() => inputRef.current?.click()} testid="import-browse" />
          <SourceCard icon={HardDrive} title="Google Drive" desc={driveConfigured ? "Kies uit je Drive" : "Configuratie vereist"} onClick={openDrive} disabled={!driveConfigured} badge={!driveConfigured ? "Owner setup" : null} testid="import-drive" />
          <SourceCard icon={Cloud} title="Dropbox" desc={dropboxConfigured ? "Kies uit Dropbox" : "Configuratie vereist"} onClick={openDropbox} disabled={!dropboxConfigured} badge={!dropboxConfigured ? "Owner setup" : null} testid="import-dropbox" />
        </div>
        <p className="mt-4 text-center text-xs text-re-muted">
          JPEG, PNG of WebP · max 20 MB per foto · max {maxPhotos} foto&apos;s · {currentCount}/{maxPhotos} geselecteerd
        </p>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPTED.join(",")} multiple hidden onChange={(e) => handleLocal(e.target.files)} data-testid="import-file-input" />
    </div>
  );
}
